/**
 * RAPOR GÖVDESİ — SEO-GEO raporunun 5 bölümlük görünümü.
 * İki yerden kullanılır: uygulama içi rapor ekranı ve /r/<token> paylaşımlı müşteri sayfası.
 * `branding` verilirse kapakta Geolop yerine AJANSIN kimliği görünür (white-label).
 */
import { GlSection, GlEmpty, GlButton } from "@/components/ui/gl-kit";
import { pdfIndir } from "@/lib/pdf-indir";
import { GlTable, GlTHead, GlTBody, GlTr, GlTh, GlTd, GlBadge } from "@/components/ui/gl-table";
import { Printer, Gauge, Link2, Bot, Users, Smartphone, Monitor } from "lucide-react";
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend } from "recharts";

export interface AgencyBranding { name?: string; logoUrl?: string; color?: string }

export type ReportData = {
  target: string; market: string; generatedAt: string; source: "existing" | "live";
  onsite: {
    healthScore: number; crawledPages: number;
    links: { healthy: number; broken: number; problem: number; redirect: number; uncrawlable: number };
    totals: { errors: number; warnings: number; notices: number };
    topIssues: { severity: "error" | "warning" | "notice"; label: string; count: number }[];
  } | null;
  speed: {
    mobile: { performance: number; lcpMs: number; cls: number; fcpMs: number; tbtMs: number } | null;
    desktop: { performance: number; lcpMs: number; cls: number; fcpMs: number; tbtMs: number } | null;
  } | null;
  offsite: {
    domainRank: number; backlinks: number; referringDomains: number;
    toxicity: { overall: string; toxicDomains: number; potentiallyToxic: number; clean: number };
    topAnchors: { anchor: string; backlinks: number }[];
  } | null;
  geo: {
    visibilityScore: number; sov: number; mentionRate: number;
    byModel: { model: string; visibilityScore: number; mentionRate: number }[];
    topBrands: { name: string; mentions: number; isUs: boolean }[];
    aiBots: { bot: string; allowed: boolean }[];
    llmsTxt: boolean | null;
    aiOverview: { checked: number; present: number; cited: number; samples: { keyword: string; present: boolean; cited: boolean; sources: string[] }[] } | null;
  } | null;
  us: { organicKeywords: number; organicTraffic: number; pos1: number; pos2_3: number; pos4_10: number } | null;
  competitors: { domain: string; commonKeywords: number; avgPosition: number; organicKeywords: number; organicTraffic: number; pos1: number; pos2_3: number; pos4_10: number; source: "auto" | "manual" }[];
  trafficTrend: { month: string; series: Record<string, number> }[];
  notes: string[];
};

const fmt = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const SEVERITY_TR = { error: "Hata", warning: "Uyarı", notice: "Bilgi" } as const;

/** Skor halkası — sağlık/performans göstergesi */
function ScoreRing({ value, label, size = 150 }: { value: number; label: string; size?: number }) {
  const color = value >= 90 ? "#12b76a" : value >= 50 ? "#f79009" : "#f04438";
  return (
    <div className="flex flex-col items-center" style={{ width: size }}>
      <div style={{ width: size, height: size }} className="relative">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart innerRadius="78%" outerRadius="100%" data={[{ value }]} startAngle={90} endAngle={-270}>
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
            <RadialBar dataKey="value" cornerRadius={12} fill={color} background={{ fill: "rgba(112,112,108,.12)" }} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-gray-800 dark:text-white/90" style={{ fontVariantNumeric: "tabular-nums" }}>
            {label.startsWith("%") ? `%${value}` : value}
          </span>
        </div>
      </div>
      <span className="mt-1 text-theme-sm font-medium text-gray-600 dark:text-gray-300">{label.replace(/^%/, "")}</span>
    </div>
  );
}

/** Yatay durum çubuğu (link durumları) */
function StatBar({ label, value, total, tone }: { label: string; value: number; total: number; tone: string }) {
  const pct = total > 0 ? Math.max(2, Math.round((value / total) * 100)) : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-theme-sm text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-theme-sm font-semibold text-gray-800 dark:text-white/90" style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(value)}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: tone }} />
      </div>
    </div>
  );
}


export function ReportBody({ d, branding }: { d: ReportData; branding?: AgencyBranding | null }) {
  const linkTotal = d.onsite ? Object.values(d.onsite.links).reduce((a, b) => a + b, 0) : 0;
  const brandName = branding?.name || "Geolop";
  const brandColor = branding?.color || "#e83e3c";
  return (
    <>
        <div id="rapor-govde" className="space-y-5">
          {/* Kapak şeridi */}
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
            <div>
              <p className="flex items-center gap-2 text-theme-xs font-semibold uppercase tracking-widest" style={{ color: brandColor }}>
                {branding?.logoUrl && <img src={branding.logoUrl} alt="" className="h-5 w-auto rounded" />}
                {brandName} · SEO-GEO Raporu
              </p>
              <h2 className="mt-1 text-2xl font-bold text-gray-800 dark:text-white/90">{d.target}</h2>
              <p className="mt-1 text-theme-sm text-gray-500">
                {new Date(d.generatedAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                {" · "}{d.market.toUpperCase()} pazarı
                {" · "}{d.source === "live" ? "canlı tarama" : "ajan verilerinden derlendi"}
              </p>
            </div>
            <GlButton variant="outline" onClick={() => void pdfIndir("#rapor-govde", "seo-geo-raporu")} className="print:hidden">
              <Printer className="h-4 w-4" /> Raporu İndir (PDF)
            </GlButton>
          </div>

          {!!d.notes.length && (
            <div className="rounded-xl border border-warning-200 bg-warning-50 p-4 text-theme-sm text-gray-700 dark:border-warning-500/30 dark:bg-warning-500/10 dark:text-gray-200 print:hidden">
              {d.notes.map((n, i) => <p key={i} className="py-0.5">• {n}</p>)}
            </div>
          )}

          {/* 1 — SİTE İÇİ */}
          <GlSection title="1 · Site İçi SEO Analizi" desc="Tarama sağlığı, link durumları ve tespit edilen sorunlar.">
            {!d.onsite ? <GlEmpty title="Site içi veri yok" icon={<Link2 className="h-6 w-6" />} /> : (
              <div className="grid gap-8 lg:grid-cols-[auto_1fr]">
                <div className="flex flex-col items-center gap-2">
                  <ScoreRing value={d.onsite.healthScore} label="%Site Sağlığı" />
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-800 dark:text-white/90" style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(d.onsite.crawledPages)}</p>
                    <p className="text-theme-xs text-gray-500">Taranan Sayfa</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <StatBar label="Sağlıklı Sayfa" value={d.onsite.links.healthy} total={linkTotal} tone="#12b76a" />
                  <StatBar label="Kırık (4xx/5xx)" value={d.onsite.links.broken} total={linkTotal} tone="#f04438" />
                  <StatBar label="Sorunlu Sayfa" value={d.onsite.links.problem} total={linkTotal} tone="#f79009" />
                  <StatBar label="Yönlendirilen" value={d.onsite.links.redirect} total={linkTotal} tone="#ec6461" />
                  <StatBar label="Taranamayan" value={d.onsite.links.uncrawlable} total={linkTotal} tone="#70706c" />
                </div>
              </div>
            )}

            {d.onsite && (
              <>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {([["Hata", d.onsite.totals.errors, "text-error-500"], ["Uyarı", d.onsite.totals.warnings, "text-warning-500"], ["Bildirim", d.onsite.totals.notices, "text-brand-500"]] as const).map(([l, v, c]) => (
                    <div key={l} className="rounded-xl border border-gray-200 p-4 text-center dark:border-gray-800">
                      <p className={`text-2xl font-bold ${c}`} style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(v)}</p>
                      <p className="text-theme-xs text-gray-500">{l}</p>
                    </div>
                  ))}
                </div>
                {!!d.onsite.topIssues.length && (
                  <div className="mt-4 overflow-x-auto">
                    <GlTable>
                      <GlTHead><GlTr><GlTh>Tür</GlTh><GlTh>Sorun</GlTh><GlTh align="right">Adet</GlTh></GlTr></GlTHead>
                      <GlTBody>
                        {d.onsite.topIssues.map((i) => (
                          <GlTr key={i.label}>
                            <GlTd><GlBadge color={i.severity === "error" ? "error" : i.severity === "warning" ? "warning" : "light"}>{SEVERITY_TR[i.severity]}</GlBadge></GlTd>
                            <GlTd>{i.label}</GlTd>
                            <GlTd align="right"><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(i.count)}</span></GlTd>
                          </GlTr>
                        ))}
                      </GlTBody>
                    </GlTable>
                  </div>
                )}
              </>
            )}
          </GlSection>

          {/* 2 — HIZ: mobil ve masaüstü AYRI (PSI görünümü) */}
          <GlSection title="2 · Site Hız Analizi" desc="Ana sayfa Lighthouse performansı — mobil ve masaüstü ayrı ölçülür.">
            {!d.speed ? <GlEmpty title="Hız verisi yok" desc="Lighthouse ölçümü bu koşuda alınamadı." icon={<Gauge className="h-6 w-6" />} /> : (
              <div className="grid gap-6 lg:grid-cols-2">
                {([["Mobil", d.speed.mobile, <Smartphone key="m" className="h-4 w-4" />], ["Masaüstü", d.speed.desktop, <Monitor key="d" className="h-4 w-4" />]] as const).map(([label, sp, icon]) => (
                  <div key={label} className="rounded-2xl border border-gray-200 p-5 dark:border-gray-800">
                    <div className="mb-4 flex items-center gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-500 dark:bg-brand-500/15">{icon}</span>
                      <span className="text-theme-sm font-semibold text-gray-800 dark:text-white/90">{label}</span>
                    </div>
                    {!sp ? (
                      <p className="text-theme-sm text-gray-400">Bu cihaz için ölçüm alınamadı.</p>
                    ) : (
                      <div className="grid gap-5 sm:grid-cols-[auto_1fr] sm:items-center">
                        <ScoreRing value={sp.performance} label="Performans" size={130} />
                        <div>
                          <div className="grid grid-cols-2 gap-2.5">
                            {([["LCP", `${(sp.lcpMs / 1000).toFixed(1)}s`, sp.lcpMs <= 2500], ["CLS", sp.cls.toFixed(2), sp.cls <= 0.1], ["FCP", `${(sp.fcpMs / 1000).toFixed(1)}s`, sp.fcpMs <= 1800], ["TBT", `${sp.tbtMs}ms`, sp.tbtMs <= 200]] as const).map(([l, v, ok]) => (
                              <div key={l} className="rounded-xl border border-gray-200 px-3 py-2.5 text-center dark:border-gray-800">
                                <p className={`text-lg font-bold ${ok ? "text-success-600" : "text-warning-600"}`} style={{ fontVariantNumeric: "tabular-nums" }}>{v}</p>
                                <p className="text-theme-xs text-gray-500">{l}</p>
                              </div>
                            ))}
                          </div>
                          {/* PSI skor lejantı */}
                          <div className="mt-3 flex items-center gap-3 text-theme-xs text-gray-500">
                            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-error-500" /> 0–49</span>
                            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-warning-500" /> 50–89</span>
                            <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-success-500" /> 90–100</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </GlSection>

          {/* 3 — SİTE DIŞI */}
          <GlSection title="3 · Site Dışı SEO Analizi" desc="Backlink profili, otorite ve toksisite dağılımı.">
            {!d.offsite ? <GlEmpty title="Backlink verisi yok" icon={<Link2 className="h-6 w-6" />} /> : (
              <>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  {([["Domain Skoru", String(d.offsite.domainRank)], ["Backlink", fmt(d.offsite.backlinks)], ["Referans Domain", fmt(d.offsite.referringDomains)], ["Toksisite", d.offsite.toxicity.overall === "High" ? "Yüksek" : d.offsite.toxicity.overall === "Medium" ? "Orta" : "Düşük"]] as const).map(([l, v]) => (
                    <div key={l} className="rounded-xl border border-gray-200 p-4 text-center dark:border-gray-800">
                      <p className="text-2xl font-bold text-gray-800 dark:text-white/90" style={{ fontVariantNumeric: "tabular-nums" }}>{v}</p>
                      <p className="text-theme-xs text-gray-500">{l}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 grid gap-6 lg:grid-cols-2">
                  <div className="space-y-3">
                    <p className="text-theme-xs font-semibold uppercase tracking-wide text-gray-500">Referans Domain Dağılımı</p>
                    <StatBar label="Temiz" value={d.offsite.toxicity.clean} total={d.offsite.referringDomains} tone="#12b76a" />
                    <StatBar label="Potansiyel Toksik" value={d.offsite.toxicity.potentiallyToxic} total={d.offsite.referringDomains} tone="#f79009" />
                    <StatBar label="Toksik" value={d.offsite.toxicity.toxicDomains} total={d.offsite.referringDomains} tone="#f04438" />
                  </div>
                  {!!d.offsite.topAnchors.length && (
                    <div>
                      <p className="mb-2 text-theme-xs font-semibold uppercase tracking-wide text-gray-500">En Çok Kullanılan Anchor'lar</p>
                      <div className="flex flex-wrap gap-1.5">
                        {d.offsite.topAnchors.map((a) => <GlBadge key={a.anchor} color="light">{a.anchor.slice(0, 32)} · {fmt(a.backlinks)}</GlBadge>)}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </GlSection>

          {/* 4 — GEO-AEO */}
          <GlSection title="4 · GEO-AEO Analizi" desc="Yapay zekâ motorlarında görünürlük ve AI botlarının siteye erişimi.">
            {!d.geo ? <GlEmpty title="AI görünürlük verisi yok" icon={<Bot className="h-6 w-6" />} /> : (
              <div className="grid gap-8 lg:grid-cols-[auto_1fr]">
                <div className="flex flex-col items-center gap-3">
                  <ScoreRing value={d.geo.visibilityScore} label="%AI Görünürlük" />
                  <div className="flex gap-4 text-center">
                    <div><p className="text-lg font-bold text-gray-800 dark:text-white/90">%{d.geo.mentionRate}</p><p className="text-theme-xs text-gray-500">Anılma</p></div>
                    <div><p className="text-lg font-bold text-gray-800 dark:text-white/90">%{d.geo.sov}</p><p className="text-theme-xs text-gray-500">Anılma Payı</p></div>
                  </div>
                </div>
                <div className="space-y-4">
                  {!!d.geo.byModel.length && (
                    <div className="space-y-3">
                      {d.geo.byModel.map((m) => <StatBar key={m.model} label={m.model} value={m.visibilityScore} total={100} tone="#e83e3c" />)}
                    </div>
                  )}
                  {d.geo.aiOverview && (
                    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-800">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="text-theme-sm font-semibold text-gray-800 dark:text-white/90">Google AI Overview</span>
                        <GlBadge color={d.geo.aiOverview.cited > 0 ? "success" : d.geo.aiOverview.present > 0 ? "warning" : "light"}>
                          {d.geo.aiOverview.checked} kelime kontrol edildi · {d.geo.aiOverview.present} tanesinde AIO çıktı · {d.geo.aiOverview.cited} tanesinde kaynak gösterildik
                        </GlBadge>
                      </div>
                      <div className="space-y-1.5">
                        {d.geo.aiOverview.samples.map((sm) => (
                          <div key={sm.keyword} className="flex flex-wrap items-center gap-1.5 text-theme-xs">
                            <GlBadge color={sm.cited ? "success" : sm.present ? "warning" : "light"}>
                              {sm.cited ? "kaynağız ✓" : sm.present ? "AIO var, biz yokuz" : "AIO çıkmadı"}
                            </GlBadge>
                            <span className="font-medium text-gray-700 dark:text-gray-300">{sm.keyword}</span>
                            {sm.sources.slice(0, 4).map((src) => (
                              <span key={src} className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-500 dark:bg-white/5 dark:text-gray-400">{src}</span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5">
                    {/* Yokluğu uyarı değil; llms.txt zorunlu bir standart değil. */}
                    {d.geo.llmsTxt != null && <GlBadge color={d.geo.llmsTxt ? "success" : "light"}>llms.txt {d.geo.llmsTxt ? "var" : "yok (isteğe bağlı)"}</GlBadge>}
                    {d.geo.aiBots.map((b) => <GlBadge key={b.bot} color={b.allowed ? "success" : "error"}>{b.bot} {b.allowed ? "erişebiliyor" : "engelli"}</GlBadge>)}
                  </div>
                  {!!d.geo.topBrands.length && (
                    <div>
                      <p className="mb-1.5 text-theme-xs font-semibold uppercase tracking-wide text-gray-500">AI Cevaplarında Anılan Markalar</p>
                      <div className="flex flex-wrap gap-1.5">
                        {d.geo.topBrands.map((b) => (
                          <GlBadge key={b.name} color={b.isUs ? "primary" : "light"} variant={b.isUs ? "solid" : "light"}>{b.name} · {b.mentions}</GlBadge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </GlSection>

          {/* 5 — RAKİP: kıyas tablosu + 12 aylık organik trafik trendi */}
          <GlSection title="5 · Rakip Analizi" desc="Elle girilen ve otomatik önerilen rakipler — organik büyüklük kıyası ve 12 aylık trafik trendi.">
            {!d.competitors.length ? <GlEmpty title="Rakip verisi bulunamadı" desc="Rapor formundaki Rakipler alanına domain girip yeniden oluşturabilirsin." icon={<Users className="h-6 w-6" />} /> : (
              <>
                <div className="overflow-x-auto">
                  <GlTable>
                    <GlTHead>
                      <GlTr>
                        <GlTh>Site</GlTh><GlTh>Kaynak</GlTh><GlTh align="right">Ortak Kelime</GlTh>
                        <GlTh align="right">Organik Kelime</GlTh><GlTh align="right">Organik Trafik</GlTh>
                        <GlTh align="right">1. Sıra</GlTh><GlTh align="right">2-3</GlTh><GlTh align="right">4-10</GlTh>
                      </GlTr>
                    </GlTHead>
                    <GlTBody>
                      {d.us && (
                        <GlTr className="bg-brand-25 dark:bg-brand-500/[0.06]">
                          <GlTd><span className="font-semibold text-brand-600 dark:text-brand-400">{d.target} (siz)</span></GlTd>
                          <GlTd>—</GlTd><GlTd align="right">—</GlTd>
                          <GlTd align="right"><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(d.us.organicKeywords)}</span></GlTd>
                          <GlTd align="right"><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(d.us.organicTraffic)}</span></GlTd>
                          <GlTd align="right">{fmt(d.us.pos1)}</GlTd><GlTd align="right">{fmt(d.us.pos2_3)}</GlTd><GlTd align="right">{fmt(d.us.pos4_10)}</GlTd>
                        </GlTr>
                      )}
                      {d.competitors.map((c) => (
                        <GlTr key={c.domain}>
                          <GlTd><span className="font-medium text-gray-800 dark:text-white/90">{c.domain}</span></GlTd>
                          <GlTd>{c.source === "manual" ? <GlBadge color="primary">elle</GlBadge> : <GlBadge color="light">otomatik</GlBadge>}</GlTd>
                          <GlTd align="right"><span style={{ fontVariantNumeric: "tabular-nums" }}>{c.commonKeywords ? fmt(c.commonKeywords) : "—"}</span></GlTd>
                          <GlTd align="right"><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(c.organicKeywords)}</span></GlTd>
                          <GlTd align="right"><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(c.organicTraffic)}</span></GlTd>
                          <GlTd align="right">{fmt(c.pos1 ?? 0)}</GlTd><GlTd align="right">{fmt(c.pos2_3 ?? 0)}</GlTd><GlTd align="right">{fmt(c.pos4_10 ?? 0)}</GlTd>
                        </GlTr>
                      ))}
                    </GlTBody>
                  </GlTable>
                </div>

                {(d.trafficTrend ?? []).length >= 2 && (() => {
                  const doms = [d.target, ...d.competitors.map((c) => c.domain)].slice(0, 6);
                  const TONES = ["#e83e3c", "#ec6461", "#b12220", "#f18e8b", "#d42b29", "#911d1c"];
                  const rows = (d.trafficTrend ?? []).map((t) => ({ ay: t.month, ...t.series }));
                  return (
                    <div className="mt-6">
                      <p className="mb-2 text-theme-xs font-semibold uppercase tracking-wide text-gray-500">Organik Trafik Trendi (12 ay, tahmini)</p>
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={rows}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e4" />
                          <XAxis dataKey="ay" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmt(v)} />
                          <RTooltip formatter={(v: number) => fmt(v)} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          {doms.map((dom, i) => (
                            <Line key={dom} type="monotone" dataKey={dom} stroke={TONES[i % TONES.length]}
                              strokeWidth={dom === d.target ? 3 : 2} dot={false}
                              strokeDasharray={dom === d.target ? undefined : "4 3"} />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                      <p className="mt-1 text-theme-xs text-gray-400">Kesintisiz çizgi: {d.target}. Değerler arama kaynaklı tahmini organik trafiktir.</p>
                    </div>
                  );
                })()}
              </>
            )}
            {d.us && (
              <div className="mt-4 grid grid-cols-3 gap-3">
                {([["1. Sıra", d.us.pos1], ["2-3. Sıra", d.us.pos2_3], ["4-10. Sıra", d.us.pos4_10]] as const).map(([l, v]) => (
                  <div key={l} className="rounded-xl border border-gray-200 p-4 text-center dark:border-gray-800">
                    <p className="text-2xl font-bold text-gray-800 dark:text-white/90" style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(v)}</p>
                    <p className="text-theme-xs text-gray-500">{l} Kelime</p>
                  </div>
                ))}
              </div>
            )}
          </GlSection>

          <p className="pb-4 text-center text-theme-xs text-gray-400">
            Bu rapor {brandName} tarafından {new Date(d.generatedAt).toLocaleDateString("tr-TR")} tarihinde üretildi
          </p>
        </div>
    </>
  );
}
